import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const LightboxContext = createContext({ open: () => {} });

export const useLightbox = () => useContext(LightboxContext);

export const LightboxProvider = ({ children }) => {
  const [state, setState] = useState(null); // { items: [{src, alt, caption, video?}], index }

  const open = useCallback((items, index = 0) => setState({ items, index }), []);
  const close = useCallback(() => setState(null), []);
  const step = useCallback(
    (d) => setState((s) => (s ? { ...s, index: (s.index + d + s.items.length) % s.items.length } : s)),
    []
  );

  const isOpen = state !== null;
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [isOpen, close, step]);

  const item = state?.items[state.index];

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {isOpen && item && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/95 p-4 sm:p-10"
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-ink"
            >
              <X size={20} />
            </button>
            {state.items.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev"
                  onClick={(e) => { e.stopPropagation(); step(-1); }}
                  aria-label="Previous photo"
                  className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-ink sm:left-6"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  data-testid="lightbox-next"
                  onClick={(e) => { e.stopPropagation(); step(1); }}
                  aria-label="Next photo"
                  className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-ink sm:right-6"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
            <motion.figure
              key={item.video || item.src}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-full flex-col items-center"
            >
              {item.video ? (
                <video
                  src={item.video}
                  poster={item.src}
                  controls
                  autoPlay
                  playsInline
                  data-testid="lightbox-video"
                  className="max-h-[78vh] max-w-full"
                />
              ) : (
                <img
                  src={item.src}
                  alt={item.alt || ""}
                  data-testid="lightbox-image"
                  className="max-h-[78vh] max-w-full object-contain"
                />
              )}
              {item.caption && (
                <figcaption
                  data-testid="lightbox-caption"
                  className="mt-5 text-center text-[11px] uppercase tracking-[0.3em] text-paper/60"
                >
                  {item.caption}
                </figcaption>
              )}
              {state.items.length > 1 && (
                <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-paper/40">
                  {state.index + 1} / {state.items.length}
                </p>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </LightboxContext.Provider>
  );
};
